import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable no-console */
/* eslint-env browser */
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import _ from 'lodash';
import entityComposition from '../../../common/classes/entityComposition';
import mapSettingsSelector from '../../selectors/mapSettingsSelector';
import MapCallout from './mapCallout';
import Loader from '../../../common/loader';
import ClusterCallout from './clusterCallout';
import { flyToView, getCurrentView } from './mapHelpers';
const mapStyleTypes = {
  standard: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  hybrid: 'mapbox://styles/mapbox/satellite-streets-v11'
};
const Map = _ref => {
  let cabinetIdentifier = _ref.cabinetIdentifier,
    _ref$cabinetFilters = _ref.cabinetFilters,
    cabinetFilters = _ref$cabinetFilters === void 0 ? {} : _ref$cabinetFilters,
    user = _ref.login.user,
    _ref$map = _ref.map,
    reduxMap = _ref$map === void 0 ? {
      currentCabinet: '',
      list: [],
      updatedFormType: ''
    } : _ref$map,
    _ref$map2 = _ref.map,
    _ref$map3 = _ref$map2 === void 0 ? {
      currentCabinet: '',
      selectedMapType: undefined,
      updatedFormType: ''
    } : _ref$map2,
    currentCabinet = _ref$map3.currentCabinet,
    selectedMapType = _ref$map3.selectedMapType,
    updatedFormType = _ref$map3.updatedFormType,
    mapActions = _ref.mapActions,
    _ref$mapSettings = _ref.mapSettings,
    mapSettings = _ref$mapSettings === void 0 ? {
      defaultMapType: 'standard',
      mapViewEnabled: false,
      rotateEnabled: false,
      scrollEnabled: true,
      showsCompass: false,
      showsScale: false,
      showsUserLocation: false,
      zoomEnabled: true
    } : _ref$mapSettings,
    selectedView = _ref.selectedView;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    selectedMarker = _useState2[0],
    setSelectedMarker = _useState2[1];
  const mapContainerRef = useRef(null);
  const calloutTargetRef = useRef(null);
  const clickedMarkerRef = useRef(false);
  const geojsonRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tokenRef = useRef(null);
  const cabinetFiltersRef = useRef(null);
  const lifecycleRef = useRef({
    programmaticMove: false,
    viewSaved: false,
    initialFlyCompleted: false,
    pipelineFitCompleted: false
  });
  const pipelineViewEnabled = useRef(false);
  const mapboxToken = user === null || user === void 0 ? void 0 : user.mapboxToken;
  if (mapboxToken) {
    tokenRef.current = mapboxToken;
  }
  const cabinetData = _objectSpread({
    bbox: [0, 0, 0, 0],
    zoom: 0
  }, reduxMap[cabinetIdentifier]);
  const indexQuery = cabinetData.indexQuery,
    _cabinetData$list = cabinetData.list,
    list = _cabinetData$list === void 0 ? [] : _cabinetData$list,
    _cabinetData$loading = cabinetData.loading,
    loading = _cabinetData$loading === void 0 ? false : _cabinetData$loading,
    _cabinetData$initiali = cabinetData.initialized,
    initialized = _cabinetData$initiali === void 0 ? false : _cabinetData$initiali;
  useEffect(() => {
    pipelineViewEnabled.current = selectedView === 'Pipeline' && (cabinetIdentifier === 'connections' || cabinetIdentifier === 'welds');
    if (pipelineViewEnabled.current) {
      lifecycleRef.current.pipelineFitCompleted = false;
    }
    const map = mapInstanceRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setLayoutProperty('weld-lines', 'visibility', pipelineViewEnabled.current ? 'visible' : 'none');
    map.setLayoutProperty('cluster-markers', 'visibility', pipelineViewEnabled.current ? 'none' : 'visible');
    map.setLayoutProperty('cluster-count', 'visibility', pipelineViewEnabled.current ? 'none' : 'visible');
    map.setLayoutProperty('unclustered-point', 'visibility', pipelineViewEnabled.current ? 'none' : 'visible');
  }, [cabinetIdentifier, selectedView]);
  const handleMapClick = (e, mapInstance) => {
    setSelectedMarker(null);
    const clusters = mapInstance.queryRenderedFeatures(e.point, {
      layers: ['cluster-markers']
    });
    const pins = mapInstance.queryRenderedFeatures(e.point, {
      layers: ['unclustered-point']
    });
    const _getCurrentView = getCurrentView(mapInstance),
      bbox = _getCurrentView.bbox,
      zoom = _getCurrentView.zoom;
    if (clusters.length) {
      const feature = clusters[0];
      const pointCount = feature.properties.point_count;
      const clusterId = feature.properties.cluster_id;
      if (pointCount > 20 || lifecycleRef.current.initialFlyCompleted) {
        lifecycleRef.current.initialFlyCompleted = false;
        const currentMapZoom = Math.floor(mapInstance.getZoom());
        mapInstance.easeTo({
          center: feature.geometry.coordinates,
          zoom: currentMapZoom + 1
        });
      } else {
        const query = _objectSpread(_objectSpread({}, indexQuery), {}, {
          bbox,
          clusterId,
          filters: _objectSpread({}, cabinetFiltersRef.current),
          zoom
        });
        setSelectedMarker(_objectSpread(_objectSpread({}, feature.properties), {}, {
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1]
        }));
        mapActions.getClusterItems(query);
      }
    } else if (pins.length) {
      const feature = pins[0];
      const itemId = feature.properties.id;
      const query = _objectSpread(_objectSpread({}, indexQuery), {}, {
        bbox,
        itemId,
        filters: _objectSpread({}, cabinetFiltersRef.current),
        zoom
      });
      setSelectedMarker(_objectSpread(_objectSpread({}, feature.properties), {}, {
        longitude: feature.geometry.coordinates[0],
        latitude: feature.geometry.coordinates[1]
      }));
      mapActions.getClusterItems(query);
    } else {
      setSelectedMarker(null);
      if (calloutTargetRef.current) {
        calloutTargetRef.current.style.display = 'none';
      }
    }
  };
  const handleMarkerDeselect = useCallback(() => {
    if (!clickedMarkerRef.current) {
      setSelectedMarker(null);
    }
  }, []);
  const loadMarkerImages = (mapInstance, pinStyles) => {
    // Always ensure the default marker is loaded
    if (!mapInstance.hasImage('marker-red')) {
      mapInstance.loadImage('../../../../../img/map/map-marker-red.png', (err, image) => {
        if (err) {
          console.error('Failed to load default marker image', err);
          return;
        }
        mapInstance.addImage('marker-red', image);
      });
    }

    // Load other markers if pinStyles exists
    if (Object.keys(pinStyles).length) {
      // eslint-disable-next-line no-unused-vars
      Object.entries(pinStyles).forEach(_ref2 => {
        let _ref3 = _slicedToArray(_ref2, 2),
          formType = _ref3[0],
          styleObj = _ref3[1];
        const color = styleObj.color;
        if (color && color === 'red') return;
        const imagePath = "../../../../../img/map/map-marker-".concat(color, ".png");
        const iconName = "marker-".concat(color);
        if (!mapInstance.hasImage(iconName)) {
          mapInstance.loadImage(imagePath, (err, image) => {
            if (err) {
              console.error("Failed to load image for ".concat(color), err);
              return;
            }
            mapInstance.addImage(iconName, image);
          });
        }
      });
    }
  };
  const buildIconImageExpression = pinStyles => {
    const expression = ['match', ['get', 'formTypeIdentifier']];
    if (Object.keys(pinStyles).length) {
      Object.entries(pinStyles).forEach(_ref4 => {
        let _ref5 = _slicedToArray(_ref4, 2),
          formType = _ref5[0],
          styleObj = _ref5[1];
        const _styleObj$color = styleObj.color,
          color = _styleObj$color === void 0 ? 'red' : _styleObj$color;
        const iconKey = "marker-".concat(color);
        expression.push(formType, iconKey);
      });
    } else {
      // If pinStyles is empty, add a dummy case to ensure the expression is valid
      expression.push('default', 'marker-red');
    }

    // Always add a default fallback
    expression.push('marker-red');
    return expression;
  };
  const iconImageExpression = useMemo(() => buildIconImageExpression(mapSettings.pinStyles), [mapSettings.pinStyles]);
  const addSourceAndLayers = mapInstance => {
    if (!mapInstance.getSource('markers')) {
      mapInstance.addSource('markers', {
        type: 'geojson',
        data: geojsonRef.current || {
          type: 'FeatureCollection',
          features: []
        }
      });
    }
    if (!mapInstance.getSource('weld-lines')) {
      mapInstance.addSource('weld-lines', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      mapInstance.addLayer({
        id: 'weld-lines',
        type: 'line',
        source: 'weld-lines',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          visibility: pipelineViewEnabled.current ? 'visible' : 'none'
        },
        paint: {
          'line-color': 'red',
          'line-width': 4,
          'line-opacity': 1
        }
      });
    }
    if (!mapInstance.getLayer('unclustered-point')) {
      mapInstance.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'markers',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': iconImageExpression,
          'icon-size': 0.15,
          'icon-anchor': 'bottom',
          'text-field': ['get', 'markerLabel'],
          'text-font': ['Arial Unicode MS Bold'],
          'text-size': ['interpolate', ['exponential', 1], ['zoom'], 8, 12,
          // At zoom 8, text size is 12.
          16, 10 // At zoom 16, text size is 10.
          ],
          'text-offset': [0, 0],
          'text-anchor': 'top',
          visibility: pipelineViewEnabled.current ? 'none' : 'visible'
        },
        paint: {
          'text-color': 'black',
          'text-halo-color': 'white',
          'text-halo-width': 2
        }
      });
    }
    if (!mapInstance.getLayer('cluster-markers')) {
      mapInstance.addLayer({
        id: 'cluster-markers',
        type: 'circle',
        source: 'markers',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': ['step', ['get', 'point_count'], 12, 100, 15, 750, 20],
          'circle-color': ['step', ['get', 'point_count'], 'red', 100, 'red', 750, 'red'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        },
        layout: {
          visibility: pipelineViewEnabled.current ? 'none' : 'visible'
        }
      });
    }
    if (!mapInstance.getLayer('cluster-count')) {
      mapInstance.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'markers',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['Arial Unicode MS Bold'],
          'text-size': 12,
          'text-allow-overlap': true,
          visibility: pipelineViewEnabled.current ? 'none' : 'visible'
        },
        paint: {
          'text-color': 'white'
        }
      });
    }
  };
  const updateCalloutPosition = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedMarker || !calloutTargetRef.current) return;
    const markerWidth = 30;
    const calloutHeight = mapSettings.calloutSize.singleHeight;
    // Project the marker's coordinates to the map's pixel space
    const point = map.project([selectedMarker.longitude, selectedMarker.latitude]);

    // Calculate the final position for the callout
    const left = point.x + markerWidth - 10; // Center horizontally
    const top = point.y + calloutHeight / 2; // Place below marker

    calloutTargetRef.current.style.left = "".concat(left, "px");
    calloutTargetRef.current.style.top = "".concat(top, "px");
    calloutTargetRef.current.style.display = 'block';
  }, [mapSettings.calloutSize.singleHeight, selectedMarker]);
  const updateWeldLines = useCallback(() => {
    var _geojsonRef$current;
    const map = mapInstanceRef.current;
    if (!map || !pipelineViewEnabled.current || !((_geojsonRef$current = geojsonRef.current) !== null && _geojsonRef$current !== void 0 && (_geojsonRef$current = _geojsonRef$current.features) !== null && _geojsonRef$current !== void 0 && _geojsonRef$current.length)) return;
    const weldFeatures = geojsonRef.current.features;
    const lineFeatures = [];
    weldFeatures.forEach(feature => {
      const aheadAssetTitle = feature.properties.aheadAssetTitle;
      if (!aheadAssetTitle) return;
      const matchingFeature = weldFeatures.find(otherFeature => otherFeature.properties.behindAssetTitle === aheadAssetTitle);
      if (matchingFeature) {
        lineFeatures.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [feature.geometry.coordinates, matchingFeature.geometry.coordinates]
          }
        });
      }
    });
    const pipelineGeoJson = {
      type: 'FeatureCollection',
      features: lineFeatures
    };
    const src = map.getSource('weld-lines');
    if (src) src.setData(pipelineGeoJson);
    if (pipelineViewEnabled.current && lineFeatures.length && !lifecycleRef.current.pipelineFitCompleted) {
      const bounds = new mapboxgl.LngLatBounds();
      lineFeatures.forEach(f => f.geometry.coordinates.forEach(c => bounds.extend(c)));
      if (!bounds.isEmpty()) {
        lifecycleRef.current.programmaticMove = true;
        map.fitBounds(bounds, {
          padding: 20,
          duration: 1000
        });
        map.once('moveend', () => {
          lifecycleRef.current.programmaticMove = false;
          lifecycleRef.current.pipelineFitCompleted = true;
        });
      }
    }
  }, []);

  // Update callout position on selectedMarker change
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedMarker) return;
    requestAnimationFrame(() => {
      updateCalloutPosition();
    });
  }, [selectedMarker, updateCalloutPosition]);
  useEffect(() => {
    if (!mapContainerRef.current || !tokenRef.current) return;
    mapboxgl.accessToken = tokenRef.current;
    const storedBBox = cabinetData.bbox,
      storedZoom = cabinetData.zoom;
    const bboxIsEmpty = storedBBox.every(v => v === 0);
    let centerToUse = [0, 0];
    let zoomToUse = 0;
    if (!bboxIsEmpty) {
      const _storedBBox = _slicedToArray(storedBBox, 4),
        w = _storedBBox[0],
        s = _storedBBox[1],
        e = _storedBBox[2],
        n = _storedBBox[3];
      centerToUse = [(w + e) / 2, (s + n) / 2];
      zoomToUse = storedZoom;
    }
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyleTypes[selectedMapType],
      center: centerToUse,
      zoom: zoomToUse,
      scrollZoom: mapSettings.zoomEnabled,
      dragPan: mapSettings.scrollEnabled,
      renderWorldCopies: false
    });

    // If we have a valid bbox, fit the map to those bounds
    if (!bboxIsEmpty) {
      const _storedBBox2 = _slicedToArray(storedBBox, 4),
        w = _storedBBox2[0],
        s = _storedBBox2[1],
        e = _storedBBox2[2],
        n = _storedBBox2[3];
      map.fitBounds([[w, s], [e, n]], {
        padding: 50
      });
    }
    map.on('load', () => {
      loadMarkerImages(map, mapSettings.pinStyles);
      addSourceAndLayers(map);
    });
    map.on('idle', () => {
      if (!lifecycleRef.current.viewSaved) {
        lifecycleRef.current.viewSaved = true;
        const _getCurrentView2 = getCurrentView(map),
          bbox = _getCurrentView2.bbox,
          zoom = _getCurrentView2.zoom;
        mapActions.setMapView(cabinetIdentifier, bbox, zoom);
        cabinetFiltersRef.current = cabinetFilters;
        const currentIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
          filters: cabinetFilters,
          bbox,
          zoom,
          mapView: true,
          pipelineView: pipelineViewEnabled.current
        });
        mapActions.serverIndex(cabinetIdentifier, currentIndexQuery);
      }
    });
    if (!mapSettings.rotateEnabled) {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
    if (mapSettings.showsUserLocation && mapSettings.scrollEnabled) {
      // Add the GeolocateControl to show the user's location
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        showUserHeading: true
      });
      map.addControl(geolocateControl, 'top-right');
    }
    if (!mapSettings.zoomEnabled) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
    }
    if (mapSettings.showsCompass || mapSettings.zoomEnabled) {
      const navControl = new mapboxgl.NavigationControl({
        showCompass: mapSettings.showsCompass,
        showZoom: mapSettings.zoomEnabled
      });
      map.addControl(navControl, 'top-right');
    }
    if (mapSettings.showsScale) {
      const scaleControl = new mapboxgl.ScaleControl({
        maxWidth: 200,
        unit: 'imperial'
      });
      map.addControl(scaleControl, 'bottom-left');
    }
    mapInstanceRef.current = map;

    // eslint-disable-next-line consistent-return
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinetIdentifier]);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.on('click', e => handleMapClick(e, map));
    map.on('mouseenter', 'unclustered-point', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'unclustered-point', () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', 'cluster-markers', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'cluster-markers', () => {
      map.getCanvas().style.cursor = '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinetIdentifier]);

  // ensures the map style is fully loaded before adding markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const onStyleLoad = () => {
      loadMarkerImages(map, mapSettings.pinStyles);
      addSourceAndLayers(map);
      if (pipelineViewEnabled.current) {
        updateWeldLines();
      }
    };
    map.off('style.load', onStyleLoad);
    map.on('style.load', onStyleLoad);
    const newStyleUrl = mapStyleTypes[selectedMapType];
    map.setStyle(newStyleUrl);

    // eslint-disable-next-line consistent-return
    return () => {
      map.off('style.load', onStyleLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMapType, updateWeldLines]);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const handleMapMove = () => {
      // Only process if this is a user-initiated move
      if (lifecycleRef.current.programmaticMove) {
        return;
      }
      clickedMarkerRef.current = false;
      handleMarkerDeselect();
      const _getCurrentView3 = getCurrentView(map),
        bbox = _getCurrentView3.bbox,
        zoom = _getCurrentView3.zoom;
      cabinetFiltersRef.current = cabinetFilters;
      mapActions.setMapView(cabinetIdentifier, bbox, zoom);
      const currentIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
        filters: cabinetFiltersRef.current,
        bbox,
        zoom,
        mapView: true
      });
      mapActions.serverIndex(cabinetIdentifier, currentIndexQuery);
    };
    const handleMapZoom = () => {
      // Only process if this is a user-initiated zoom
      if (lifecycleRef.current.programmaticMove) {
        return;
      }
      clickedMarkerRef.current = false;
      handleMarkerDeselect();
    };
    // Add event listeners for user interactions
    map.on('moveend', handleMapMove);
    map.on('zoomend', handleMapZoom);
    map.on('dragend', handleMapMove);

    // Cleanup event listeners when the component unmounts
    // eslint-disable-next-line consistent-return
    return () => {
      map.off('moveend', handleMapMove);
      map.off('zoomend', handleMapZoom);
      map.off('dragend', handleMapMove);
    };
  }, [cabinetFilters, cabinetIdentifier, indexQuery, mapActions, selectedMarker, handleMarkerDeselect]);

  // server data retrieval
  useEffect(() => {
    if (!lifecycleRef.current.viewSaved) return;
    if (cabinetIdentifier !== currentCabinet || indexQuery && !_.isEqual(cabinetFilters, indexQuery.filters) || updatedFormType) {
      const _getCurrentView4 = getCurrentView(mapInstanceRef.current),
        bbox = _getCurrentView4.bbox,
        zoom = _getCurrentView4.zoom;
      cabinetFiltersRef.current = cabinetFilters;
      mapActions.setMapView(cabinetIdentifier, bbox, zoom);
      const currentIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
        filters: cabinetFiltersRef.current,
        bbox,
        zoom,
        mapView: true,
        pipelineView: pipelineViewEnabled.current
      });
      if (updatedFormType) {
        currentIndexQuery.initialized = false;
      }
      setSelectedMarker(null);
      mapActions.serverIndex(cabinetIdentifier, currentIndexQuery);
    }
  }, [cabinetFilters, cabinetIdentifier, currentCabinet, indexQuery, loading, mapActions, updatedFormType]);

  // Separate effect for view changes
  useEffect(() => {
    if (!initialized) return;
    const _getCurrentView5 = getCurrentView(mapInstanceRef.current),
      bbox = _getCurrentView5.bbox,
      zoom = _getCurrentView5.zoom;
    cabinetFiltersRef.current = cabinetFilters;
    const currentIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
      filters: cabinetFiltersRef.current,
      bbox,
      zoom,
      mapView: true,
      pipelineView: pipelineViewEnabled.current
    });
    setSelectedMarker(null);
    mapActions.serverIndex(cabinetIdentifier, currentIndexQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView]);

  // Effect to update markers when list changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !list.length) return;
    if (list && list.length && list[0]) {
      const newData = list[0];
      if (JSON.stringify(geojsonRef.current) !== JSON.stringify(newData)) {
        geojsonRef.current = newData;
        const source = map.getSource('markers');
        if (source) {
          source.setData(newData);
        }
        if (!initialized && !loading) {
          const setProgrammaticMove = value => {
            lifecycleRef.current.programmaticMove = value;
            if (value) {
              // Use a one-time event listener to reset the flag when the animation completes
              const resetFlag = () => {
                lifecycleRef.current.programmaticMove = false;
                map.off('moveend', resetFlag);
              };
              map.once('moveend', resetFlag);
            }
          };
          flyToView(newData, map, setProgrammaticMove, pipelineViewEnabled.current);
          mapActions.setMapInitialized(cabinetIdentifier);
          lifecycleRef.current.initialFlyCompleted = true;
        }
      }
    }
    if (pipelineViewEnabled.current) {
      updateWeldLines();
    }
  }, [list, loading, initialized, cabinetIdentifier, mapActions, updateWeldLines]);
  return mapSettings.mapViewEnabled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, loading && !initialized && /*#__PURE__*/React.createElement(Loader, {
    style: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      position: 'absolute',
      top: 150,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000
    }
  }), /*#__PURE__*/React.createElement("div", {
    "data-testid": "map-container",
    style: {
      width: '100%',
      height: '500px',
      position: 'relative'
    },
    ref: mapContainerRef
  })), /*#__PURE__*/React.createElement("div", {
    "data-testid": "callout-target",
    ref: calloutTargetRef,
    id: "callout-target",
    style: {
      position: 'absolute',
      display: 'none',
      width: '1px',
      height: '1px'
    }
  }), selectedMarker && !selectedMarker.cluster && /*#__PURE__*/React.createElement(MapCallout, {
    key: selectedMarker.id,
    cabinetIdentifier: cabinetIdentifier,
    selectedMarker: selectedMarker,
    mapInstance: mapInstanceRef.current,
    setSelectedMarker: setSelectedMarker,
    target: calloutTargetRef.current
  }), selectedMarker && selectedMarker.cluster && /*#__PURE__*/React.createElement(ClusterCallout, {
    key: selectedMarker.id,
    cabinetIdentifier: cabinetIdentifier,
    selectedMarker: selectedMarker,
    mapInstance: mapInstanceRef.current,
    setSelectedMarker: setSelectedMarker,
    target: calloutTargetRef.current
  })) : null;
};
Map.displayName = 'Map';
Map.propTypes = {
  cabinetFilters: PropTypes.shape({}),
  cabinetIdentifier: PropTypes.string.isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      mapboxToken: PropTypes.string
    })
  }).isRequired,
  map: PropTypes.shape({
    currentCabinet: PropTypes.string,
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({})
    }),
    list: PropTypes.arrayOf(PropTypes.shape({
      latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })),
    loading: PropTypes.bool,
    selectedMapType: PropTypes.string,
    updatedFormType: PropTypes.string
  }),
  mapActions: PropTypes.shape({
    getClusterItems: PropTypes.func,
    initializeMapbox: PropTypes.func,
    serverIndex: PropTypes.func.isRequired,
    setMapInitialized: PropTypes.func.isRequired,
    setMapType: PropTypes.func.isRequired,
    setMapView: PropTypes.func.isRequired
  }).isRequired,
  mapSettings: PropTypes.shape({
    calloutSize: PropTypes.shape({
      width: PropTypes.number,
      singleHeight: PropTypes.number,
      clusterHeight: PropTypes.number
    }),
    defaultMapType: PropTypes.string,
    mapViewEnabled: PropTypes.bool,
    pinStyles: PropTypes.shape({}).isRequired,
    rotateEnabled: PropTypes.bool,
    scrollEnabled: PropTypes.bool,
    showsCompass: PropTypes.bool,
    showsScale: PropTypes.bool,
    showsUserLocation: PropTypes.bool,
    zoomEnabled: PropTypes.bool
  }),
  selectedView: PropTypes.string.isRequired
};
export { Map as DisconnectedMap };
export default entityComposition(Map, ['map', 'login'], {
  mapSettings: mapSettingsSelector
}, []);